import { sanitizeUrl } from '@braintree/sanitize-url';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ButtonLink from 'common/components/Elements/ButtonLink/ButtonLink';
import ButtonLoading from 'common/components/Elements/ButtonLoading/ButtonLoading';
import ExpandableText from 'common/components/Elements/ExpandableText/ExpandableText';
import Icon, { CalculatorIcon } from 'common/components/Elements/Icon/Icon';
import SanitizeHTML from 'common/components/Elements/SanitizeHTML/SanitizeHTML';
import { consoleWarn } from 'common/utilities/browser/consoleLog/consoleLog';
import { clientUrlPath } from 'common/utilities/browser/url/url';
import { isDarkColorString, styleBackgroundColor } from 'common/utilities/colors/colors';
import { generateRandomString, stringContainsLineBreak, stringEndsWidth } from 'common/utilities/core/string';
import useFileDownload from 'common/utilities/hooks/useFileDownload/useFileDownload';
import {
  isCollectionItem, isItemWithValue, isMultiUnitValuesItem, isValueEmpty,
} from 'core/api/api.typesGuards';
import * as I from 'core/api/coreApiClient';
import { isFileDownloadUrl } from 'core/api/url';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import { formatDate,formatDatetime } from 'core/utilities/dateFormat/dateFormat';
import type { SxStyles } from 'core/utilities/theme';
import theme from 'core/utilities/theme';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import Image from './Image/Image';
import Sequence from './Sequence/Sequence';
import classes from './Value.styles';
import ValueIcon from './ValueIcon/ValueIcon';

const MAX_TEXT_LENGTH_IN_TABLE_CELL = 500;
const MAX_ITEMS_IN_TABLE_CELL = 50;

interface ValueLinkProps { value: string, url: string, sx?: SxStyles, buttonStyle?: boolean }

function FileDownloadLink({
  value, url, sx, buttonStyle,
}: ValueLinkProps) {
  const [loading, setLoading] = useState(false);
  const { FileDownloadLoader, onStart } = useFileDownload(() => {
    setLoading(false);
  });

  const sanitizedUrl = sanitizeUrl(url);
  const asDownloadFileUrl = clientUrlPath(`download-file?url=${encodeURIComponent(sanitizedUrl)}`);
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    // ctrl+click will open the link in a new tab, don't start the download here
    if (!e.ctrlKey) {
      e.preventDefault();
      setLoading(true);
      onStart(sanitizedUrl, e);
    }
  };

  return (
    <Box position="relative" display="inline-block" data-testid="fileDownload">
      {buttonStyle ? (
        <ButtonLink
          action="secondary"
          url={asDownloadFileUrl}
          onClick={handleClick}
          externalLink
        >
          {value}
        </ButtonLink>
      ) : (
        <Link
          href={asDownloadFileUrl}
          onClick={handleClick}
          sx={sx}
        >
          {value}
        </Link>
      )}

      {loading && (
        <ButtonLoading />
      )}
      {FileDownloadLoader && <FileDownloadLoader />}
    </Box>
  );
}

export function ExternalLink({
  value, url, sx, buttonStyle,
}: ValueLinkProps) {
  if (isFileDownloadUrl(url)) {
    return (<FileDownloadLink value={value} url={url} sx={sx} buttonStyle={buttonStyle} />);
  }

  return (
    <Link href={url} target="_blank" sx={sx}>
      {value}
    </Link>
  );
}

export function InternalLink({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value, url, sx, buttonStyle,
}: ValueLinkProps) {
  return (
    <Link component={RouterLink} to={sanitizeUrl(url)} sx={sx}>
      {value}
    </Link>
  );
}

export function ContentWithLineBreak({ content }: { content: string }) {
  return (
    <span style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
      <SanitizeHTML html={content} disallowedTagsMode="escape" />
    </span>
  );
}

interface FormattedValueProps {
  info: I.Info;
  valueInfo?: I.ValueInfo;
  item: I.BaseItem;
  inTable?: boolean;
  disabled?: boolean;
}

function FormattedValue({
  info, valueInfo, item, inTable, disabled,
}: FormattedValueProps) {
  const configuration = useSelector((state: ApplicationState) => state.configuration.configuration);
  const { id, type, autoCalculated } = info;

  let valueRender;

  switch (type) {
    case I.TypeConstants.Boolean:
      valueRender = (item.value as string | boolean).toString();
      break;
    case I.TypeConstants.Float:
    case I.TypeConstants.Integer:
    case I.TypeConstants.String:
      if (type === I.TypeConstants.String && stringContainsLineBreak(item.value)) {
        valueRender = (
          <ExpandableText
            text={item.value}
            maxLength={inTable ? MAX_TEXT_LENGTH_IN_TABLE_CELL : Infinity}
            renderer={(text, button) => (
              <>
                <ContentWithLineBreak content={text} />
                {button}
              </>
            )}
          />
        );
      } else if (stringEndsWidth(id, 'alias')) {
        valueRender = <span style={{ wordBreak: 'break-all' }}>{item.value}</span>;
      } else if (typeof item.value === 'string' && item.value.match('</')) {
        // Ensure we don't use ExpandableText for values containing HTML
        valueRender = <SanitizeHTML html={item.value} disallowedTagsMode="escape" />;
      } else if (isMultiUnitValuesItem(item)) {
        const value = item.values.find((v) => v.unitId === item.unitId);

        valueRender = value?.value;
      } else {
        valueRender = (
          <span style={{ wordBreak: 'break-word' }}>
            <ExpandableText
              text={item.value}
              maxLength={inTable ? MAX_TEXT_LENGTH_IN_TABLE_CELL : Infinity}
              renderer={(text, button) => (
                <>
                  <SanitizeHTML html={text} disallowedTagsMode="escape" />
                  {button}
                </>
              )}
            />
          </span>
        );
      }
      break;
    case I.TypeConstants.Date:
      valueRender = formatDate(item.value, configuration);
      break;
    case I.TypeConstants.DateWithTime:
      valueRender = formatDatetime(item.value, configuration);
      break;
    case I.TypeConstants.ExternalLink:
      if (item.value) {
        if (item.url) {
          valueRender = <ExternalLink value={item.value} url={item.url} buttonStyle={info.useShowView} />;
        } else {
          valueRender = <span>{item.value}</span>;
        }
      }
      break;
    case I.TypeConstants.Image:
      valueRender = <Image imgUrl={item.value as string} linkUrl={item.url} inTable={inTable} />;
      break;
    case I.TypeConstants.InternalLink:
      if (item.value) {
        if (item.url) {
          if (id === 'qualifiedId') {
            valueRender = (
              <span style={{ whiteSpace: 'nowrap' }}>
                <InternalLink value={item.value} url={item.url} />
              </span>
            );
          } else {
            valueRender = <InternalLink value={item.value} url={item.url} sx={{ wordBreak: 'break-word' }} />;
          }
        } else {
          valueRender = <span>{item.value}</span>;
        }
      }
      break;
    case I.TypeConstants.Sequence:
      valueRender = (
        <ExpandableText
          text={item.value}
          maxLength={2000}
          renderer={(text, button) => (
            <Typography
              variant="body2"
              component="span"
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                '&::after': {
                  display: !inTable && button && text.length !== (item.value as string).length ? 'block' : 'none',
                  position: 'absolute',
                  bottom: theme.spacing(3),
                  height: theme.spacing(3),
                  width: '100%',
                  background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 1) 100%)',
                  content: '" "',
                  zIndex: 1,
                },
              }}
            >
              <Sequence id={id} value={{ ...item, value: text }} inTable={inTable} elementAfter={button} />
            </Typography>
          )}
          buttonIconStyle="arrows"
        />
      );
      break;
    case I.TypeConstants.ValueSpecific:
      // Special case where a list of value from the same info can have multiple type
      // In this case, we don't use the 'type' present in I.info, but the type in I.ValueInfo
      if (valueInfo) {
        if (valueInfo.type === I.TypeConstants.ValueSpecific) {
           
          const message = `VALUE TYPE ERROR: ${id} valueInfo.type can't be of type ValueSpecific, but needs to provide the actual type.`;
          consoleWarn(message);
          valueRender = undefined;
        } else if (valueInfo.type) {
          valueRender = (
            <FormattedValue
              info={{ ...info, type: valueInfo.type }}
              item={item}
              valueInfo={valueInfo}
              inTable={inTable}
            />
          );
        }
      } else {
        valueRender = undefined;
      }
      break;
    default:
      valueRender = undefined;
      break;
  }

  return (
    <Typography variant="body2" component="span" sx={disabled ? classes.disabled : undefined}>
      {autoCalculated && <span title="Calculated value"><CalculatorIcon mr /></span>}
      {valueRender}
      {valueInfo && valueInfo.icon && <ValueIcon icon={valueInfo.icon} inTable={inTable} />}
    </Typography>
  );
}

interface FormattedValuesProps {
  info: I.Info;
  valueInfo?: I.ValueInfo;
  item: I.CollectionItem;
  inTable?: boolean;
  disabled?: boolean;
}

function FormattedValues({
  info, valueInfo, item, inTable, disabled,
}: FormattedValuesProps) {
  const [expanded, setExpanded] = useState(false);

  const maxShownItems = inTable ? MAX_ITEMS_IN_TABLE_CELL : Infinity;
  const limitedItems = item.items.slice(0, maxShownItems);
  const hiddenItems = item.items.length - maxShownItems;

  return (
    <>
      {(expanded ? item.items : limitedItems).map((listItem, index) => {
        if (isItemWithValue(listItem)) {
          return (
            <React.Fragment key={generateRandomString()}>
              {index > 0 ? ', ' : ''}
              <FormattedValue
                info={info}
                item={listItem}
                valueInfo={valueInfo}
                inTable={inTable}
                disabled={disabled}
              />
            </React.Fragment>
          );
        }
        return null;
      })}

      {hiddenItems > 0 && (
        <Box sx={{ display: 'inline-block' }}>
          {expanded ? (
            <IconButton
              onClick={() => setExpanded(false)}
              size="small"
              title={I18n.Elements.Value.FormattedValues.collapse}
              sx={{ p: theme.spacing(0, 1) }}
              data-e2e-items-collapse=""
            >
              <Icon element={RemoveIcon} />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setExpanded(true)}
              size="small"
              title={I18n.Elements.Value.FormattedValues.expand(hiddenItems)}
              sx={{ p: theme.spacing(0, 1) }}
              data-e2e-items-expand=""
            >
              <Icon element={MoreHorizIcon} />
            </IconButton>
          )}
        </Box>
      )}
    </>
  );
}

export interface ComponentProps {
  info: I.Info;
  value: I.Value;
  inTable?: boolean;
  /** Grey out the value, disable links */
  disabled?: boolean;
}

function Value({
  info, value, inTable, disabled,
}: ComponentProps) {
  if (isValueEmpty(value)) {
    return null;
  }

  const { item, info: valueInfo } = value;

  const backgroundColor = valueInfo?.backgroundColor;
  const isDarkBackground = backgroundColor && isDarkColorString(backgroundColor);

  if (item) {
    return (
      <Box
        component="span"
        data-e2e-property={info.id}
        style={styleBackgroundColor(backgroundColor)}
        className={backgroundColor ? 'withBackgroundColor' : ''}
        sx={isDarkBackground ? classes.withDarkBackgroundColor : undefined}
        title={valueInfo?.title}
      >
        {isCollectionItem(item)
          ? (
            <FormattedValues
              info={info}
              item={item}
              valueInfo={valueInfo}
              inTable={inTable}
              disabled={disabled}
            />
          )
          : (
            <FormattedValue
              info={info}
              item={item}
              valueInfo={valueInfo}
              inTable={inTable}
              disabled={disabled}
            />
          )}
      </Box>
    );
  }

  return null;
}

export default Value;
